import { FC, useMemo } from 'react';
import { Checkbox, Flex, Heading } from '@/_components';
import { CATEGORY_ORDER } from '@/_constants/notifications';
import { getOrderedNotifications } from '@/_utils/notifications';
import type { NotificationListProps } from './NotificationList.types';

export const NotificationList: FC<NotificationListProps> = ({
  isGranular,
  excludeConversation,
  questionnaireDocumentEnabled,
  selectedNotifications,
  onToggle,
  isDisabled,
}) => {
  const allNotifications = useMemo(
    () =>
      getOrderedNotifications({
        isGranular,
        excludeConversation,
        questionnaireDocumentEnabled,
      }),
    [isGranular, excludeConversation, questionnaireDocumentEnabled],
  );

  const groupedNotifications = useMemo(() => {
    if (!isGranular) return {};

    return allNotifications.reduce((acc: any, notification) => {
      const category = notification.data.category;
      if (!acc[category]) acc[category] = [];
      acc[category].push(notification);
      return acc;
    }, {});
  }, [isGranular, allNotifications]);

  const filteredCategoryOrder = useMemo(() => {
    return CATEGORY_ORDER.filter((category) => groupedNotifications[category]);
  }, [groupedNotifications]);

  if (!isGranular) {
    // Flat list of broad notification types (flag OFF)
    return (
      <Flex isColumn={true} gap={8}>
        {allNotifications.map(({ id, data }) => (
          <Checkbox
            key={id}
            onChange={() => onToggle(id)}
            isChecked={selectedNotifications.has(id)}
            isDisabled={isDisabled?.(id) ?? !data.isEnabled}
            helperText={data.description}
            label={data.name}
          />
        ))}
      </Flex>
    );
  }

  // Grouped by category (flag ON)
  return (
    <Flex isColumn={true} gap={12}>
      {filteredCategoryOrder.map((category) => {
        const notifications = groupedNotifications[category];

        return (
          <Flex key={category} isColumn={true} gap={8}>
            <Heading level={4}>{category}</Heading>
            <Flex isColumn={true} gap={6}>
              {notifications.map(({ id, data }: any) => (
                <Checkbox
                  key={id}
                  onChange={() => onToggle(id)}
                  isChecked={selectedNotifications.has(id)}
                  isDisabled={isDisabled?.(id) ?? !data.isEnabled}
                  helperText={data.description}
                  label={data.name}
                />
              ))}
            </Flex>
          </Flex>
        );
      })}
    </Flex>
  );
};
